import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Upload,
  FileText,
  AlertCircle,
  CheckCircle,
  X,
  Loader2,
  ExternalLink,
  AlertTriangle,
} from 'lucide-react';

import { docsService } from '../../services/docs/docsService';
import type { ImportJob, ImportItem } from '../../services/docs/types';

const ACCEPTED_TYPES: Record<string, string> = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    '.docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/zip': '.zip',
  'application/pdf': '.pdf',
};

const POLL_INTERVAL_MS = 3000;
const TERMINAL_STATUSES = new Set([
  'completed',
  'completed_with_warnings',
  'failed',
]);

const DocsImportPage: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<ImportJob | null>(null);
  const [items, setItems] = useState<ImportItem[]>([]);
  const [polling, setPolling] = useState(false);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setPolling(false);
  }, []);

  const pollJob = useCallback(
    async (jobId: string) => {
      try {
        const result = await docsService.getImportJob(jobId);
        setJob(result.job);
        setItems(result.items);

        if (TERMINAL_STATUSES.has(result.job.status)) {
          stopPolling();
        } else {
          pollTimerRef.current = setTimeout(
            () => pollJob(jobId),
            POLL_INTERVAL_MS
          );
        }
      } catch {
        stopPolling();
      }
    },
    [stopPolling]
  );

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setError(null);
    setJob(null);
    setItems([]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setError(null);
    setJob(null);
    setItems([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;

    try {
      setUploading(true);
      setError(null);
      setJob(null);
      setItems([]);
      setUploadProgress('Requesting upload URL...');

      const contentType = selectedFile.type || 'application/octet-stream';
      const { uploadUrl, s3Key } = await docsService.requestUploadUrl(
        selectedFile.name,
        contentType
      );

      setUploadProgress('Uploading file...');
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: selectedFile,
      });

      if (!uploadResponse.ok) {
        throw new Error(
          `Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`
        );
      }

      setUploadProgress('Starting import...');
      const { job: createdJob } = await docsService.createImportJob(
        mode,
        s3Key
      );
      setJob(createdJob);
      setUploadProgress(null);
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';

      // Start polling
      setPolling(true);
      pollTimerRef.current = setTimeout(
        () => pollJob(createdJob.id),
        POLL_INTERVAL_MS
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to upload and import'
      );
      setUploadProgress(null);
    } finally {
      setUploading(false);
    }
  };

  const isTerminal = job && TERMINAL_STATUSES.has(job.status);
  const isSuccess =
    job?.status === 'completed' || job?.status === 'completed_with_warnings';
  const isFailed = job?.status === 'failed';

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/docs')}
        className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to Docs
      </button>

      <h1 className="text-2xl font-bold text-gray-900 mb-2">
        Import Documents
      </h1>
      <p className="text-gray-500 mb-6">
        Upload DOCX, XLSX, PDF, or ZIP files to import as procedures.
      </p>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Job status */}
      {job && (
        <div
          className={`rounded-lg border p-4 mb-6 ${
            isFailed
              ? 'bg-red-50 border-red-200'
              : isSuccess
                ? 'bg-green-50 border-green-200'
                : 'bg-blue-50 border-blue-200'
          }`}
        >
          <div className="flex items-start gap-3">
            {isFailed ? (
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            ) : isSuccess ? (
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <Loader2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5 animate-spin" />
            )}
            <div className="flex-1 min-w-0">
              <p
                className={`font-medium ${
                  isFailed
                    ? 'text-red-800'
                    : isSuccess
                      ? 'text-green-800'
                      : 'text-blue-800'
                }`}
              >
                {isFailed
                  ? 'Import failed'
                  : isSuccess
                    ? 'Import completed'
                    : 'Processing import...'}
              </p>
              <p
                className={`text-sm mt-1 ${
                  isFailed
                    ? 'text-red-700'
                    : isSuccess
                      ? 'text-green-700'
                      : 'text-blue-700'
                }`}
              >
                Status: {job.status.replace(/_/g, ' ')}
                {isTerminal &&
                  job.totalFiles > 0 &&
                  ` · ${job.successfulFiles} of ${job.totalFiles} file(s) imported`}
              </p>

              {/* Import items */}
              {items.length > 0 && (
                <div className="mt-3 space-y-2">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between text-sm bg-white rounded-md px-3 py-2 border border-gray-100"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {item.status === 'completed' ? (
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        ) : item.status === 'failed' ? (
                          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        ) : item.status === 'warning' ? (
                          <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                        ) : (
                          <Loader2 className="w-4 h-4 text-blue-500 flex-shrink-0 animate-spin" />
                        )}
                        <span className="text-gray-700 truncate">
                          {item.sourcePath}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                        {item.errorMessage && (
                          <span className="text-xs text-red-500">
                            {item.errorMessage}
                          </span>
                        )}
                        {item.warningMessage && (
                          <span className="text-xs text-yellow-600">
                            {item.warningMessage}
                          </span>
                        )}
                        {item.procedureId && (
                          <Link
                            to={`/docs/procedures/${item.procedureId}`}
                            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                          >
                            View
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Single item shortcut: if exactly one completed item with a procedureId */}
              {isSuccess && items.length === 1 && items[0].procedureId && (
                <Link
                  to={`/docs/procedures/${items[0].procedureId}`}
                  className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Open imported document
                  <ExternalLink className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg border border-gray-200 p-6"
      >
        <div className="space-y-5">
          {/* Mode selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Import Mode
            </label>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="single"
                  checked={mode === 'single'}
                  onChange={() => setMode('single')}
                  className="mr-2"
                />
                <FileText className="w-4 h-4 mr-1 text-gray-500" />
                Single File
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="bulk"
                  checked={mode === 'bulk'}
                  onChange={() => setMode('bulk')}
                  className="mr-2"
                />
                <Upload className="w-4 h-4 mr-1 text-gray-500" />
                Bulk (ZIP)
              </label>
            </div>
          </div>

          {/* File drop zone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File
            </label>
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              className="relative flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-8 cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={Object.values(ACCEPTED_TYPES).join(',')}
                onChange={handleFileSelect}
                className="hidden"
              />
              {selectedFile ? (
                <div className="flex items-center gap-3">
                  <FileText className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">
                    Drop a file here, or click to browse
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    DOCX, XLSX, PDF, or ZIP
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={uploading || !selectedFile || polling}
            className="flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            <Upload className="w-4 h-4 mr-1.5" />
            {uploading ? uploadProgress || 'Processing...' : 'Start Import'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DocsImportPage;
